/**
 * AuthLogo
 * ========
 * Presentational logo for the public auth pages. Variant-aware so the same
 * component serves both the light pages (Login, ForgotPassword) and the dark
 * ResetPassword page without regressing per-page styling.
 *
 * Render priority:
 *   1. not yet `resolved`     → height-reserving placeholder (no wrong logo, no layout shift)
 *   2. branding.logo_url      → org logo <img>
 *   3. branding.name          → org name text
 *   4. otherwise (no org)     → nothing (no logo shown)
 *
 * The placeholder reserves the variant's logo height so gating the logo on fetch
 * completion never causes a layout shift.
 */

import React from 'react';

const VARIANTS = {
  light: {
    height: 'h-10',
    img: 'h-10 w-auto max-w-[200px] object-contain inline-block',
    defaultImg: 'h-10 w-auto inline-block',
    name: 'text-xl font-bold text-neutral-900',
  },
  dark: {
    height: 'h-16',
    img: 'h-16 w-auto max-w-[200px] object-contain',
    defaultImg: 'h-16 w-auto',
    name: 'text-2xl font-bold text-white',
  },
};

const AuthLogo = ({ branding, resolved, variant = 'light' }) => {
  const styles = VARIANTS[variant] || VARIANTS.light;

  if (!resolved) {
    // Reserve the logo's height to prevent a layout shift once it resolves.
    return <div className={styles.height} aria-hidden="true" />;
  }

  if (branding?.logo_url) {
    return <img src={branding.logo_url} alt={branding.name} className={styles.img} />;
  }

  if (branding?.name) {
    return <span className={styles.name}>{branding.name}</span>;
  }

  // No organization → show no logo at all.
  return null;
};

export default AuthLogo;
